"use client";

import { cn } from "./utils";
import { motion } from "motion/react";
import { Trophy, Medal, Award, ChevronRight } from "lucide-react";

export interface LeaderboardItem {
  id: string;
  rank: number;
  name: string;
  subtitle?: string;
  value: number;
  secondaryValue?: string;
  details?: { label: string; value: string }[];
  status?: "accepted" | "pending" | "declined" | "expired" | "reviewing" | "negotiating";
  onClick?: () => void;
}

export interface FundraisingLeaderboardProps {
  items: LeaderboardItem[];
  formatValue?: (value: number) => string;
  emptyMessage?: string;
  maxItems?: number;
  className?: string;
}

/**
 * A leaderboard component for displaying ranked items like term sheets.
 * Shows rank medals for top 3, with status badges and click actions.
 */
export function FundraisingLeaderboard({
  items,
  formatValue = defaultFormatCurrency,
  emptyMessage = "No items yet",
  maxItems = 5,
  className,
}: FundraisingLeaderboardProps) {
  const displayItems = items.slice(0, maxItems);

  if (items.length === 0) {
    return (
      <div className={cn("p-8 text-center text-neutral-500", className)}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn("divide-y divide-neutral-100", className)}>
      {displayItems.map((item, index) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.05 }}
          className={cn(
            "p-4 flex items-start gap-4 transition-colors",
            item.onClick && "hover:bg-neutral-50 cursor-pointer"
          )}
          onClick={item.onClick}
        >
          {/* Rank badge */}
          <div className="pt-1">
            <RankBadge rank={item.rank} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium text-sm truncate">{item.name}</div>
              <div className="text-right block sm:hidden">
                 <div className="font-semibold">{formatValue(item.value)}</div>
              </div>
            </div>
            
            {item.subtitle && (
              <div className="text-xs text-neutral-500 truncate mb-2">{item.subtitle}</div>
            )}

            {/* Expanded Details Grid */}
            {item.details && item.details.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                {item.details.map((detail, i) => (
                  <div key={i} className="bg-neutral-50 rounded p-2 text-xs">
                    <div className="text-neutral-400 mb-0.5">{detail.label}</div>
                    <div className="font-medium text-neutral-700">{detail.value}</div>
                  </div>
                ))}
              </div>
            ) : (
              item.secondaryValue && (
                <div className="text-xs text-neutral-500 text-right sm:text-left">{item.secondaryValue}</div>
              )
            )}
          </div>

          {/* Value (Hidden on mobile if using details layout to save space, but shown in flex row above) */}
          <div className="text-right hidden sm:block">
            <div className="font-semibold">{formatValue(item.value)}</div>
            {!item.details && item.secondaryValue && (
              <div className="text-xs text-neutral-500">{item.secondaryValue}</div>
            )}
          </div>

          {/* Status & Action */}
          <div className="flex flex-col items-end gap-2 pt-1">
            {item.status && <StatusBadge status={item.status} />}
            
            {item.onClick && (
              <ChevronRight className="w-4 h-4 text-neutral-300 mt-1" />
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  const getRankStyles = () => {
    switch (rank) {
      case 1:
        return {
          bg: "bg-amber-100",
          text: "text-amber-700",
          icon: <Trophy className="w-3.5 h-3.5" />,
        };
      case 2:
        return {
          bg: "bg-neutral-200",
          text: "text-neutral-600",
          icon: <Medal className="w-3.5 h-3.5" />,
        };
      case 3:
        return {
          bg: "bg-orange-100",
          text: "text-orange-700",
          icon: <Award className="w-3.5 h-3.5" />,
        };
      default:
        return {
          bg: "bg-neutral-100",
          text: "text-neutral-500",
          icon: null,
        };
    }
  };

  const styles = getRankStyles();

  return (
    <div
      className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0",
        styles.bg,
        styles.text
      )}
    >
      {styles.icon || rank}
    </div>
  );
}

function StatusBadge({ status }: { status: LeaderboardItem["status"] }) {
  const getStatusStyles = () => {
    switch (status) {
      case "accepted":
        return "bg-emerald-100 text-emerald-700";
      case "declined":
        return "bg-red-100 text-red-700";
      case "expired":
        return "bg-neutral-100 text-neutral-500";
      case "negotiating":
        return "bg-blue-100 text-blue-700";
      case "reviewing":
        return "bg-purple-100 text-purple-700";
      case "pending":
      default:
        return "bg-amber-100 text-amber-700";
    }
  };

  return (
    <span className={cn("text-xs px-2 py-1 rounded-full capitalize", getStatusStyles())}>
      {status}
    </span>
  );
}

function defaultFormatCurrency(value: number): string {
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
